import { FC, memo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

interface IPropertyListItemProps {
  id: string;
  name: string;
  address?: string;
  propertyId?: string;
  imageUrl?: string;
  isSelected?: boolean;
  isImported?: boolean;
  isSynced?: boolean;
  lastSyncTime?: string;
  onSelect?: (id: string) => void;
  isRtl?: boolean;
  showCheckbox?: boolean;
  showStatus?: boolean;
}

const Container = styled.div<{ $isSelected?: boolean; $isRtl?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #F2F4F7;
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${(props) => (props.$isSelected ? '#F8F7FF' : 'transparent')};
  direction: ${(props) => (props.$isRtl ? 'rtl' : 'ltr')};

  &:hover {
    background: #F8F7FF;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Checkbox = styled.div<{ $isSelected?: boolean }>`
  width: 24px;
  height: 24px;
  border: 2px solid ${(props) => (props.$isSelected ? '#5B3FFF' : '#D0D5DD')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$isSelected ? '#5B3FFF' : 'white')};
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    color: white;
  }
`;

const PropertyImage = styled.div`
  width: 64px;
  height: 64px;
  background: #F2F4F7;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #E3E8EF 0%, #D0D5DD 100%);
  color: #9CA3AF;
  font-size: 20px;
`;

const PropertyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PropertyName = styled.h4`
  font-weight: 600;
  font-size: 14px;
  color: #130080;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PropertyDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PropertyId = styled.span`
  font-size: 12px;
  color: #9CA3AF;
`;

const PropertyAddress = styled.span`
  font-size: 12px;
  color: #697586;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SyncInfo = styled.span`
  font-size: 12px;
  color: #697586;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Dot = styled.span`
  color: #D0D5DD;
  font-size: 12px;
`;

const StatusBadge = styled.div<{ $status: 'synced' | 'not_synced' | 'pending' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${(props) => {
    switch (props.$status) {
      case 'synced':
        return '#ECFDF3';
      case 'pending':
        return '#FFFAEB';
      default:
        return '#FEF3F2';
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case 'synced':
        return '#027A48';
      case 'pending':
        return '#B54708';
      default:
        return '#B42318';
    }
  }};
`;

const StatusDot = styled.div<{ $status: 'synced' | 'not_synced' | 'pending' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.$status) {
      case 'synced':
        return '#12B76A';
      case 'pending':
        return '#F79009';
      default:
        return '#F04438';
    }
  }};
`;

const CheckIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.6667 3.5L5.25 9.91667L2.33333 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PropertyListItem: FC<IPropertyListItemProps> = memo(({
  id,
  name,
  address,
  propertyId,
  imageUrl,
  isSelected = false,
  isImported = false,
  isSynced = true,
  lastSyncTime,
  onSelect,
  isRtl = false,
  showCheckbox = true,
  showStatus = false,
}) => {
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    if (onSelect && !isImported) {
      onSelect(id);
    }
  }, [id, onSelect, isImported]);

  const getStatus = (): 'synced' | 'not_synced' | 'pending' => {
    if (!isImported) return 'not_synced';
    return isSynced ? 'synced' : 'not_synced';
  };

  const getStatusText = () => {
    const status = getStatus();
    switch (status) {
      case 'synced':
        return t('CHANNELS.SYNCED') || 'Synced';
      case 'pending':
        return t('CHANNELS.PENDING') || 'Pending';
      default:
        return t('CHANNELS.NOT_SYNCED') || 'Not Synced';
    }
  };

  return (
    <Container
      $isSelected={isSelected}
      $isRtl={isRtl}
      onClick={handleClick}
      style={{ opacity: isImported && !showStatus ? 0.6 : 1, cursor: isImported && !showStatus ? 'not-allowed' : 'pointer' }}
    >
      {showCheckbox && (
        <Checkbox $isSelected={isSelected}>
          {isSelected && <CheckIcon />}
        </Checkbox>
      )}

      <PropertyImage>
        {imageUrl ? (
          <img src={imageUrl} alt={name} />
        ) : (
          <PlaceholderImage>🏠</PlaceholderImage>
        )}
      </PropertyImage>

      <PropertyInfo>
        <PropertyName>{name}</PropertyName>
        <PropertyDetails>
          {propertyId && <PropertyId>#{propertyId}</PropertyId>}
          {propertyId && (address || lastSyncTime) && <Dot>•</Dot>}
          {lastSyncTime && (
            <SyncInfo>
              {t('CHANNELS.LAST_SYNC') || 'Last sync'}: {lastSyncTime}
            </SyncInfo>
          )}
          {lastSyncTime && address && <Dot>•</Dot>}
          {address && <PropertyAddress>{address}</PropertyAddress>}
        </PropertyDetails>
      </PropertyInfo>

      {showStatus && (
        <StatusBadge $status={getStatus()}>
          <StatusDot $status={getStatus()} />
          {getStatusText()}
        </StatusBadge>
      )}
    </Container>
  );
});

PropertyListItem.displayName = 'PropertyListItem';

export default PropertyListItem;
